import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { styles } from './safety-report-modal.style.js';
import { getLocationsByCompany, getAssetsByCompany, createSafetyReport, updateSafetyReport } from '@cmms/api';
import { db } from '../../services/auth.service.js';
import { SafetyReportType, SafetyReportStatus } from '@cmms/models';

@customElement('safety-report-modal')
export class SafetyReportModal extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ type: Object }) report: any = null;
  @property({ type: String }) companyId: string = '';
  @state() private locations: any[] = [];
  @state() private assets: any[] = [];
  @state() private isLoadingData = false;

  static styles = styles;

  @state() private formData: any = {
    title: '',
    description: '',
    report_type: SafetyReportType.SAFETY_OBSERVATION,
    status: SafetyReportStatus.OPEN,
    location_id: '',
    associated_asset_id: ''
  };
  @state() private loading = false;
  @state() private error = '';

  connectedCallback() {
    super.connectedCallback();
    this.loadLocationsAndAssets();
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    
    // Only process updates if we have companyId
    if (!this.companyId) return;
    
    if (changedProperties.has('report')) {
      if (this.report) {
        // Editing existing report - initialize form data after loading locations/assets
        this.loadLocationsAndAssets().then(() => {
          this.initializeFormData();
        });
      } else {
        // Creating new report - reset form data
        this.resetFormData();
      }
    }
  }

  private resetFormData() {
    this.formData = {
      title: '',
      description: '',
      report_type: SafetyReportType.SAFETY_OBSERVATION,
      status: SafetyReportStatus.OPEN,
      location_id: '',
      associated_asset_id: ''
    };
    this.error = '';
  }

  private initializeFormData() {
    if (!this.report) return;
    
    this.formData = {
      title: this.report.title || '',
      description: this.report.description || '',
      report_type: this.report.report_type || SafetyReportType.SAFETY_OBSERVATION,
      status: this.report.status || SafetyReportStatus.OPEN,
      location_id: this.report.location_id || this.report.location || '',
      associated_asset_id: this.report.associated_asset_id || ''
    };
    
    // Normalize the selected location and asset after data is loaded
    this.updateComplete.then(() => {
      this.normalizeSelectedLocation();
      this.normalizeSelectedAsset();
    });
  }

  private normalizeSelectedLocation() {
    const current = this.formData?.location_id;
    
    if (!current || current === '') return;
    
    // First try to match by ID (if current is already an ID)
    let match = this.locations.find(l => l.id === current);
    
    // If no match by ID, try to match by name (if current is a location name)
    if (!match) {
      match = this.locations.find(l => l.name === current);
    }
    
    
    if (match) {
      this.formData = { ...this.formData, location_id: match.id };
    }
  }

  private normalizeSelectedAsset() {
    const current = this.formData?.associated_asset_id;
    if (!current || current === '') return;
    
    const match = this.assets.find(a => a.id === current);
    if (match) {
      this.formData = { ...this.formData, associated_asset_id: match.id };
    }
  }

  private async loadLocationsAndAssets() {
    if (!this.companyId) return;
    
    try {
      this.isLoadingData = true;
      const [locationsResult, assetsResult] = await Promise.all([
        getLocationsByCompany(db, this.companyId),
        getAssetsByCompany(db, this.companyId)
      ]);

      if (locationsResult.data) {
        this.locations = locationsResult.data.map(item => ({
          id: item.id,
          ...(item.model as any).toJSON()
        }));
      }
      if (assetsResult.data) {
        this.assets = assetsResult.data.map(item => ({
          id: item.id,
          ...(item.model as any).toJSON()
        }));
      }
      
      // After loading data, normalize selections if we have a report
      if (this.report) {
        this.normalizeSelectedLocation();
        this.normalizeSelectedAsset();
      }
    } catch (error) {
      console.error('Error loading locations and assets:', error);
    } finally {
      this.isLoadingData = false;
    }
  }

  private async saveReport() {
    if (!this.validateForm()) {
      return;
    }

    this.loading = true;
    this.error = '';

    try {
      const response = this.report 
        ? await updateSafetyReport(db, this.report.id, this.formData)
        : await createSafetyReport(db, this.formData);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      this.dispatchEvent(new CustomEvent('safety-report-saved', {
        detail: { report: this.formData },
        bubbles: true
      }));
      
      this.closeModal();
    } catch (err) {
      this.error = 'Failed to save safety report';
      console.error('Error saving safety report:', err);
    } finally {
      this.loading = false;
    }
  }

  private validateForm() {
    if (!this.formData.title.trim()) {
      this.error = 'Title is required';
      return false;
    }
    if (!this.formData.description.trim()) {
      this.error = 'Description is required';
      return false;
    }
    if (!this.formData.location_id.trim()) {
      this.error = 'Location is required';
      return false;
    }
    return true;
  }

  private closeModal() {
    this.dispatchEvent(new CustomEvent('modal-closed', { bubbles: true, composed: true }));
    this.open = false;
  }

  private onInputChange(field: string, value: any) {
    this.formData = { ...this.formData, [field]: value };
  }

  render() {
    if (!this.open) return html``;

    return html`
      <sl-dialog
        label="${this.report ? 'Edit Safety Report' : 'New Safety Report'}"
        .open=${this.open}
        @sl-hide=${this.closeModal}
      >
        ${this.loading ? html`
          <div class="loading-overlay">
            <sl-spinner></sl-spinner>
          </div>
        ` : ''}

        <form @submit=${(e: Event) => { e.preventDefault(); this.saveReport(); }} 
          @click=${(e: Event) => e.stopPropagation()} 
          @sl-input=${(e: Event) => e.stopPropagation()} 
          @sl-change=${(e: Event) => e.stopPropagation()}
          @sl-open-change=${(e: Event) => e.stopPropagation()}
          @sl-show=${(e: Event) => e.stopPropagation()}
          @sl-hide=${(e: Event) => e.stopPropagation()}
        >
          <div class="form-container">
            ${this.error ? html`
              <sl-alert variant="danger" open>
                <sl-icon name="exclamation-triangle" slot="icon"></sl-icon>
                ${this.error}
              </sl-alert>
            ` : ''}

            <div class="form-group">
              <label class="form-label">
                Title <span class="required">*</span>
              </label>
              <sl-input
                value="${this.formData.title}"
                placeholder="Enter report title"
                @input="${(e: any) => this.onInputChange('title', e.target.value)}"
                @click=${(e: Event) => e.stopPropagation()}
              ></sl-input>
            </div>

          <div class="form-group">
            <label class="form-label">
              Description <span class="required">*</span>
            </label>
            <sl-textarea
              value="${this.formData.description}"
              placeholder="Describe the safety concern in detail"
              rows="4"
              @input="${(e: any) => this.onInputChange('description', e.target.value)}"
              @click=${(e: Event) => e.stopPropagation()}
            ></sl-textarea>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">
                Report Type <span class="required">*</span>
              </label>
              <sl-select
                value="${this.formData.report_type}"
                @sl-change="${(e: any) => this.onInputChange('report_type', e.target.value)}"
                @sl-open-change=${(e: Event) => e.stopPropagation()}
                @sl-show=${(e: Event) => e.stopPropagation()}
                @sl-hide=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
              >
                <sl-option value="${SafetyReportType.SAFETY_OBSERVATION}">Safety Observation</sl-option>
                <sl-option value="${SafetyReportType.NEAR_MISS}">Near Miss</sl-option>
                <sl-option value="${SafetyReportType.INCIDENT}">Incident</sl-option>
              </sl-select>
            </div>

            <div class="form-group">
              <label class="form-label">
                Status <span class="required">*</span>
              </label>
              <sl-select
                value="${this.formData.status}"
                @sl-change="${(e: any) => this.onInputChange('status', e.target.value)}"
                @sl-open-change=${(e: Event) => e.stopPropagation()}
                @sl-show=${(e: Event) => e.stopPropagation()}
                @sl-hide=${(e: Event) => e.stopPropagation()}
                @click=${(e: Event) => e.stopPropagation()}
              >
                <sl-option value="${SafetyReportStatus.OPEN}">Open</sl-option>
                <sl-option value="${SafetyReportStatus.ASSIGNED}">Assigned</sl-option>
                <sl-option value="${SafetyReportStatus.INVESTIGATING}">Investigating</sl-option>
                <sl-option value="${SafetyReportStatus.RESOLVED}">Resolved</sl-option>
              </sl-select>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">
              Location <span class="required">*</span>
            </label>
            <sl-select
              value="${this.formData.location_id || ''}"
              placeholder="Select location"
              @sl-change="${(e: any) => this.onInputChange('location_id', e.target.value)}"
              @sl-open-change=${(e: Event) => e.stopPropagation()}
              @sl-show=${(e: Event) => e.stopPropagation()}
              @sl-hide=${(e: Event) => e.stopPropagation()}
              @click=${(e: Event) => e.stopPropagation()}
            >
              <sl-option value="">Select a location</sl-option>
              ${this.locations.map(location => {
                return html`<sl-option value="${location.id}">${location.name || location.id}</sl-option>`;
              })}
            </sl-select>
          </div>

          <div class="form-group">
            <label class="form-label">Associated Asset</label>
            <sl-select
              value="${this.formData.associated_asset_id || ''}"
              placeholder="Select asset (optional)"
              @sl-change="${(e: any) => this.onInputChange('associated_asset_id', e.target.value)}"
              @sl-open-change=${(e: Event) => e.stopPropagation()}
              @sl-show=${(e: Event) => e.stopPropagation()}
              @sl-hide=${(e: Event) => e.stopPropagation()}
              @click=${(e: Event) => e.stopPropagation()}
            >
              <sl-option value="">Select an asset (optional)</sl-option>
              ${this.assets.map(asset => html`
                <sl-option value="${asset.id}">${asset.name || asset.asset_name || asset.id}</sl-option>
              `)}
            </sl-select>
          </div>
          </div>

          <div class="form-actions">
            <sl-button @click="${this.closeModal}">Cancel</sl-button>
            <sl-button 
              variant="primary" 
              type="submit"
              ?loading="${this.loading}"
            >
              ${this.report ? 'Update Report' : 'Create Report'}
            </sl-button>
          </div>
        </form>
      </sl-dialog>
    `;
  }
}
